import { supabase } from './supabase';
import * as Sentry from '@sentry/react';

const AVATAR_BUCKET = 'avatars';
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const TARGET_SIZE = 400; // 400x400px

/**
 * Check if avatars bucket exists by attempting to list files
 * Note: listBuckets() requires service role, so we check by trying to list files instead
 */
async function ensureAvatarsBucket(): Promise<boolean> {
  try {
    // Try to list files in the bucket (this works with regular auth)
    const { error } = await supabase.storage
      .from(AVATAR_BUCKET)
      .list('', { limit: 1 });
    
    if (error) {
      // If error code is 404, bucket doesn't exist
      if (error.message?.includes('not found') || error.message?.includes('does not exist')) {
        console.warn(`[avatarUpload] Bucket "${AVATAR_BUCKET}" does not exist. Please create it in Supabase dashboard with public access.`);
        return false;
      }
      // Other errors (like permission errors) we'll assume bucket exists
      console.log('[avatarUpload] Bucket check inconclusive, proceeding with upload:', error);
      return true;
    }
    
    return true;
  } catch (error) {
    console.error('[avatarUpload] Error checking bucket:', error);
    Sentry.captureException(error, {
      tags: { feature: 'avatar-upload', action: 'check-bucket' }
    });
    // If we can't check, assume bucket exists and let upload fail with better error
    return true;
  }
}

/**
 * Validate file before upload
 */
function validateFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please use JPG, PNG, or WebP images.'
    };
  }
  
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: 'File is too large. Maximum size is 2MB.'
    };
  }
  
  return { valid: true };
}

/**
 * Resize and compress image using Canvas API
 */
async function optimizeImage(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Calculate dimensions (square crop from center)
        const size = Math.min(img.width, img.height);
        const offsetX = (img.width - size) / 2;
        const offsetY = (img.height - size) / 2;
        
        // Set canvas size to target
        canvas.width = TARGET_SIZE;
        canvas.height = TARGET_SIZE;
        
        // Draw cropped and resized image
        ctx.drawImage(
          img,
          offsetX, offsetY, size, size, // Source rectangle (cropped)
          0, 0, TARGET_SIZE, TARGET_SIZE // Destination rectangle (resized)
        );
        
        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
          },
          'image/jpeg',
          0.85 // 85% quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Delete old avatar from storage
 */
async function deleteOldAvatar(oldAvatarUrl: string | null): Promise<void> {
  if (!oldAvatarUrl) return;
  
  try {
    // Extract file path from URL
    // URL format: https://{project}.supabase.co/storage/v1/object/public/avatars/{filename}
    const urlParts = oldAvatarUrl.split(`${AVATAR_BUCKET}/`);
    if (urlParts.length < 2) return;
    
    const filePath = urlParts[1];
    
    const { error } = await supabase.storage
      .from(AVATAR_BUCKET)
      .remove([filePath]);
    
    if (error) {
      console.warn('[avatarUpload] Could not delete old avatar:', error);
      // Don't throw - not critical if old file can't be deleted
    }
  } catch (error) {
    console.warn('[avatarUpload] Error deleting old avatar:', error);
    // Don't throw - not critical
  }
}

/**
 * Upload avatar to Supabase Storage
 * Returns the public URL of the uploaded avatar
 */
export async function uploadAvatar(
  file: File,
  userId: string,
  oldAvatarUrl: string | null = null
): Promise<{ url: string | null; error: string | null }> {
  try {
    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      return { url: null, error: validation.error || 'Invalid file' };
    }
    
    // Ensure bucket exists
    const bucketExists = await ensureAvatarsBucket();
    if (!bucketExists) {
      return { 
        url: null, 
        error: 'Storage not configured. Please contact support.' 
      };
    }
    
    // Optimize image
    const optimizedBlob = await optimizeImage(file);
    
    // Generate filename: {userId}-{timestamp}.jpg
    const timestamp = Date.now();
    const filename = `${userId}-${timestamp}.jpg`;
    
    // Upload to Supabase Storage
    const { data, error: uploadError } = await supabase.storage
      .from(AVATAR_BUCKET)
      .upload(filename, optimizedBlob, {
        contentType: 'image/jpeg',
        cacheControl: '3600',
        upsert: false
      });
    
    if (uploadError) {
      console.error('[avatarUpload] Upload error:', uploadError);
      Sentry.captureException(uploadError, {
        tags: { feature: 'avatar-upload', action: 'upload' }
      });
      return { url: null, error: 'Failed to upload avatar. Please try again.' };
    }
    
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(AVATAR_BUCKET)
      .getPublicUrl(filename);
    
    // Delete old avatar (non-blocking)
    if (oldAvatarUrl) {
      deleteOldAvatar(oldAvatarUrl).catch(err => {
        console.warn('[avatarUpload] Could not delete old avatar:', err);
      });
    }
    
    return { url: publicUrl, error: null };
  } catch (error) {
    console.error('[avatarUpload] Unexpected error:', error);
    Sentry.captureException(error, {
      tags: { feature: 'avatar-upload', action: 'upload' }
    });
    return { 
      url: null, 
      error: 'An unexpected error occurred. Please try again.' 
    };
  }
}

/**
 * Get avatar URL or null
 */
export function getAvatarUrl(path: string | null): string | null {
  if (!path) return null;
  
  // If it's already a full URL, return as is
  if (path.startsWith('http')) {
    return path;
  }
  
  // Otherwise, generate public URL
  const { data: { publicUrl } } = supabase.storage
    .from(AVATAR_BUCKET)
    .getPublicUrl(path);
  
  return publicUrl;
}
